import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  ReferenceArea,
} from "recharts";
import { Card, Kpi, SectionTitle } from "../ui";
import { MetricTimeline, FilterChip, ActivityTable, ZoneTooltip, tipProps, axisProps } from "../charts";
import {
  bucketOf,
  day,
  Granularity,
  Metric,
  Period,
  Discipline as Disc,
  isDistanceDiscipline,
  trendKindOf,
  trendValue,
  trendReversed,
} from "@/lib/dash";
import { Activity, DashboardData } from "@/lib/types";
import { fmtNum, fmtHr, fmtPace, fmtSpeed, fmtPace100, fmtDate, fmtDMY, hrZone, CAT_COLORS } from "@/lib/format";
import { useUI } from "@/lib/theme";

const localDateString = (ts: number) => {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}T00:00:00`;
};

const HR_ZONE_COLORS: Record<Disc, string[]> = {
  Carrera: ["#eab308", "#f97316", "#dc2626", "#991b1b", "#450a0a"],
  Ciclismo: ["#93c5fd", "#3b82f6", "#2563eb", "#1d4ed8", "#1e3a8a"],
  Natacion: ["#67e8f9", "#22d3ee", "#06b6d4", "#0891b2", "#155e75"],
  Hybrid: ["#ddd6fe", "#a78bfa", "#8b5cf6", "#7c3aed", "#4c1d95"],
  Hike: ["#bbf7d0", "#86efac", "#22c55e", "#16a34a", "#14532d"],
  Musculacion: ["#fde68a", "#fbbf24", "#f59e0b", "#d97706", "#92400e"],
};

/**
 * Tab genérica de una disciplina (running, ciclismo, natación, hybrid/fuerza, senderismo).
 * Unifica lo que antes eran las tabs Carrera y Hyrox: KPIs + seguimiento de métricas + tabla,
 * y el donut de zonas de FC. Las disciplinas de distancia añaden además una tendencia de
 * velocidad/esfuerzo propia: ritmo (min/km) en carrera/senderismo, velocidad (km/h) en ciclismo,
 * ritmo /100m en natación y esfuerzo relativo en Hybrid (con arrastre para fijar un periodo).
 */
export default function Discipline({
  cat,
  acts,
  zones,
  gearMap,
  granularity,
}: {
  cat: Disc;
  acts: Activity[];
  zones: DashboardData["zones"];
  gearMap: Record<string, string>;
  granularity: Granularity;
}) {
  const { t, loc, palette } = useUI();
  const COLOR = CAT_COLORS[cat];
  const isDistance = isDistanceDiscipline(cat);
  const items = useMemo(() => acts.filter((a) => a.categoria === cat), [acts, cat]);
  const hasPower = useMemo(() => items.some((a) => a.weighted_power_watts || a.avg_watts), [items]);
  const hasCadence = useMemo(() => items.some((a) => a.avg_cadence), [items]);
  const hasSwolf = useMemo(() => items.some((a) => a.avg_swolf), [items]);
  const hasKj = useMemo(() => items.some((a) => a.kilojoules), [items]);
  // Tendencia propia de la disciplina: ritmo (min/km), velocidad (km/h), ritmo /100m o esfuerzo.
  const trendKind = trendKindOf(cat);
  const sessionTrendKind: "pace" | "speed" | "pace100" | "power" | "effort" | null =
    cat === "Hybrid" ? "effort" : cat === "Ciclismo" && hasPower ? "power" : cat === "Hike" ? null : trendKind;
  const hasSessionTrend = sessionTrendKind !== null;
  const fmtTrend = (v: number | null | undefined): string =>
    sessionTrendKind === "effort"
      ? v != null && !Number.isNaN(v)
        ? fmtNum(v, 0, loc)
        : "-"
      : sessionTrendKind === "power"
        ? v != null && !Number.isNaN(v)
          ? `${fmtNum(v, 0, loc)} W`
          : "-"
      : sessionTrendKind === "speed"
        ? fmtSpeed(v ?? undefined, loc)
        : sessionTrendKind === "pace100"
          ? fmtPace100(v ?? undefined)
          : fmtPace(v ?? undefined);
  const trendTitle =
    sessionTrendKind === "effort"
      ? t("sec_evol_esfuerzo")
      : sessionTrendKind === "power"
        ? t("sec_evol_potencia")
      : sessionTrendKind === "speed"
        ? t("sec_evol_velocidad")
        : sessionTrendKind === "pace100"
          ? t("sec_evol_ritmo100")
          : t("sec_evol_ritmo");
  const trendLineName =
    sessionTrendKind === "effort"
      ? t("th_esfuerzo")
      : sessionTrendKind === "power"
        ? t("d_potencia")
      : sessionTrendKind === "speed"
        ? t("d_velocidad")
        : t("d_ritmo");

  // Eje continuo del rango (por día o semana) y métricas seleccionables.
  const weeks = useMemo(
    () => Array.from(new Set(acts.map((a) => bucketOf(a.start_local, granularity)))).sort(),
    [acts, granularity],
  );
  const fmtMetricNum = (v: number | null, decimals: number, l: string) => fmtNum(v ?? 0, decimals, l);
  const metrics: Metric[] = useMemo(() => {
    const base: Metric[] = [
      { key: "sesiones", label: t("m_sesiones"), agg: "count", fmt: (v, l) => fmtMetricNum(v, 0, l), color: COLOR },
      {
        key: "horas",
        label: t("m_horas"),
        agg: "sum",
        field: (a) => a.duration_min / 60,
        fmt: (v, l) => `${fmtMetricNum(v, 1, l)} h`,
        color: COLOR,
      },
    ];
    if (isDistance) {
      base.push({
        key: "km",
        label: t("m_km"),
        agg: "sum",
        field: (a) => a.distance_km,
        fmt: (v, l) => `${fmtMetricNum(v, 1, l)} km`,
        color: COLOR,
      });
    }
    base.push(
      {
        key: "cal",
        label: t("m_cal"),
        agg: "sum",
        field: (a) => a.total_calories,
        fmt: (v, l) => `${fmtMetricNum(v, 0, l)} kcal`,
        color: COLOR,
      },
      {
        key: "hr",
        label: t("m_hr"),
        agg: "avg",
        field: (a) => a.avg_heartrate,
        fmt: (v, l) => fmtHr(v, l),
        color: COLOR,
      },
    );
    if (cat === "Ciclismo") {
      base.push({
        key: "power",
        label: t("m_power"),
        agg: "avg",
        field: (a) => a.weighted_power_watts ?? a.avg_watts,
        fmt: (v, l) => (v == null ? "-" : `${fmtNum(v, 0, l)} W`),
        color: COLOR,
      });
    }
    if (isDistance && cat !== "Natacion") {
      base.push({
        key: "elev",
        label: t("m_elev"),
        agg: "sum",
        field: (a) => a.elev,
        fmt: (v, l) => `${fmtMetricNum(v, 0, l)} m`,
        color: COLOR,
      });
    }
    if (trendKind) {
      base.push({
        key: trendKind,
        label: trendKind === "speed" ? t("m_speed") : trendKind === "pace100" ? t("m_pace100") : t("m_pace"),
        agg: "avg",
        field: (a) => trendValue(a, trendKind),
        fmt: (v) => fmtTrend(v),
        color: COLOR,
      });
    }
    if (hasCadence) {
      base.push({
        key: "cadence",
        label: t("m_cadence"),
        agg: "avg",
        field: (a) => a.avg_cadence,
        fmt: (v, l) => (v == null ? "-" : `${fmtNum(v, 0, l)} rpm`),
        color: COLOR,
      });
    }
    if (cat === "Natacion" && hasSwolf) {
      base.push({
        key: "swolf",
        label: t("m_swolf"),
        agg: "avg",
        field: (a) => a.avg_swolf,
        fmt: (v, l) => (v == null ? "-" : fmtNum(v, 0, l)),
        color: COLOR,
      });
    }
    if (cat === "Ciclismo" && hasKj) {
      base.push({
        key: "kj",
        label: t("m_kj"),
        agg: "sum",
        field: (a) => a.kilojoules,
        fmt: (v, l) => `${fmtMetricNum(v, 0, l)} kJ`,
        color: COLOR,
      });
    }
    return base;
  }, [t, COLOR, isDistance, trendKind, cat, hasPower, hasCadence, hasSwolf, hasKj]); // eslint-disable-line react-hooks/exhaustive-deps

  const [period, setPeriod] = useState<Period | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  // Los filtros se limpian al cambiar de rango; el punto, también al cambiar de periodo.
  useEffect(() => {
    setPeriod(null);
    setSelectedItem(null);
  }, [acts]);
  useEffect(() => setSelectedItem(null), [period]);

  // Selección por ARRASTRE sobre el gráfico de tendencia: fija un periodo.
  const [paceDrag, setPaceDrag] = useState<{ a: number; b: number } | null>(null);
  const draggedRef = useRef(false);
  const finalizePace = () =>
    setPaceDrag((d) => {
      if (d && d.a !== d.b) {
        const [a, b] = d.a <= d.b ? [d.a, d.b] : [d.b, d.a];
        const startTs = serie.find((p) => p.idx === a)?.ts;
        const endTs = serie.find((p) => p.idx === b)?.ts;
        if (startTs == null || endTs == null) return null;
        const start = new Date(startTs);
        const end = new Date(endTs);
        end.setDate(end.getDate() + 1); // exclusivo
        draggedRef.current = true;
        setSelectedItem(null);
        setPeriod({
          start,
          end,
          label: `${fmtDate(localDateString(startTs), loc)} – ${fmtDate(localDateString(endTs), loc)}`,
        });
      }
      return null;
    });
  const paceMouse = {
    onMouseDown: (s: any) => {
      if (s?.activeLabel) setPaceDrag({ a: Number(s.activeLabel), b: Number(s.activeLabel) });
    },
    onMouseMove: (s: any) => {
      if (s?.activeLabel) setPaceDrag((d) => (d ? { ...d, b: Number(s.activeLabel) } : d));
    },
    onMouseUp: finalizePace,
    onMouseLeave: finalizePace,
  };

  // Solo el periodo (alimenta el gráfico de ritmo, que no debe anularse a sí mismo).
  const periodItems = useMemo(() => {
    if (!period) return items;
    return items.filter((a) => {
      const d = new Date(a.start_local);
      return d >= period.start && d < period.end;
    });
  }, [items, period]);

  // Conjunto filtrado (periodo + punto seleccionado) → KPIs y tabla.
  const filtered = useMemo(
    () => (selectedItem ? periodItems.filter((a) => a.id === selectedItem) : periodItems),
    [periodItems, selectedItem],
  );
  const tableActs = useMemo(
    () => [...filtered].sort((a, b) => b.start_local.localeCompare(a.start_local)),
    [filtered],
  );

  // KPIs sobre el conjunto filtrado.
  const km = filtered.reduce((s, r) => s + r.distance_km, 0);
  const horas = filtered.reduce((s, r) => s + r.duration_min, 0) / 60;
  const elev = filtered.reduce((s, r) => s + r.elev, 0);
  const carga = filtered.reduce((s, r) => s + r.relative_effort, 0);
  const kcal = filtered.reduce((s, r) => s + (r.total_calories ?? 0), 0);
  const hrs = filtered.map((r) => r.avg_heartrate).filter((h): h is number => !!h);
  const avgHr = hrs.length ? hrs.reduce((s, h) => s + h, 0) / hrs.length : 0;
  const powers = filtered.map((r) => r.weighted_power_watts ?? r.avg_watts).filter((v): v is number => !!v);
  const avgPower = powers.length ? powers.reduce((s, v) => s + v, 0) / powers.length : null;
  const cadences = filtered.map((r) => r.avg_cadence).filter((v): v is number => !!v);
  const avgCadence = cadences.length ? cadences.reduce((s, v) => s + v, 0) / cadences.length : null;
  const swolfs = filtered.map((r) => r.avg_swolf).filter((v): v is number => !!v);
  const avgSwolf = swolfs.length ? swolfs.reduce((s, v) => s + v, 0) / swolfs.length : null;
  const pace100s = filtered.map((r) => trendValue(r, "pace100")).filter((v): v is number => !!v);
  const avgPace100 = pace100s.length ? pace100s.reduce((s, v) => s + v, 0) / pace100s.length : null;
  const kpis = (() => {
    if (cat === "Ciclismo") {
      return [
        { label: t("kpi_sesiones"), value: fmtNum(filtered.length, 0, loc) },
        { label: t("kpi_horas"), value: fmtNum(horas, 1, loc) },
        { label: t("kpi_km"), value: fmtNum(km, 0, loc) },
        { label: t("kpi_calorias"), value: fmtNum(kcal, 0, loc), info: t("info_calorias") },
        { label: t("m_power"), value: avgPower ? `${fmtNum(avgPower, 0, loc)} W` : "-" },
      ];
    }
    if (cat === "Natacion") {
      return [
        { label: t("kpi_sesiones"), value: fmtNum(filtered.length, 0, loc) },
        { label: t("kpi_horas"), value: fmtNum(horas, 1, loc) },
        { label: t("kpi_km"), value: fmtNum(km, 1, loc) },
        { label: t("kpi_calorias"), value: fmtNum(kcal, 0, loc), info: t("info_calorias") },
        { label: hasSwolf ? t("m_swolf") : t("m_pace100"), value: hasSwolf ? fmtNum(avgSwolf ?? 0, 0, loc) : fmtPace100(avgPace100) },
      ];
    }
    if (cat === "Hike") {
      return [
        { label: t("kpi_sesiones"), value: fmtNum(filtered.length, 0, loc) },
        { label: t("kpi_horas"), value: fmtNum(horas, 1, loc) },
        { label: t("kpi_km"), value: fmtNum(km, 0, loc) },
        { label: t("kpi_calorias"), value: fmtNum(kcal, 0, loc), info: t("info_calorias") },
        { label: t("m_elev"), value: `${fmtNum(elev, 0, loc)} m` },
      ];
    }
    return [
      { label: t("kpi_sesiones"), value: fmtNum(filtered.length, 0, loc) },
      { label: t("kpi_horas"), value: fmtNum(horas, 1, loc) },
      isDistance
        ? { label: t("kpi_km"), value: fmtNum(km, 0, loc) }
        : { label: t("kpi_carga_total"), value: fmtNum(carga, 0, loc), info: t("info_carga") },
      { label: t("kpi_calorias"), value: fmtNum(kcal, 0, loc), info: t("info_calorias") },
      { label: t("kpi_avg_hr"), value: fmtHr(avgHr, loc) },
    ];
  })();

  // Tendencia de ritmo/velocidad/esfuerzo: cada punto es una actividad clicable.
  const serie = useMemo(
    () =>
      hasSessionTrend
        ? [...periodItems]
            .map((r) => ({
              r,
              val:
                sessionTrendKind === "effort"
                  ? r.relative_effort
                  : sessionTrendKind === "power"
                    ? (r.weighted_power_watts ?? r.avg_watts)
                    : trendValue(r, sessionTrendKind!),
            }))
            .filter((x): x is { r: Activity; val: number } => x.val != null)
            .sort((a, b) => a.r.start_local.localeCompare(b.r.start_local))
            .map(({ r, val }, idx) => ({
              id: r.id,
              idx,
              ts: day(r.start_local.slice(0, 10)).getTime(),
              val: Number(val.toFixed(2)),
            }))
        : [],
    [periodItems, hasSessionTrend, sessionTrendKind],
  );

  const dragAnchor = serie[0]?.idx;
  const dragLo = paceDrag ? (paceDrag.a <= paceDrag.b ? paceDrag.a : paceDrag.b) : dragAnchor;
  const dragHi = paceDrag ? (paceDrag.a <= paceDrag.b ? paceDrag.b : paceDrag.a) : dragAnchor;

  const selItem = selectedItem ? (items.find((r) => r.id === selectedItem) ?? null) : null;
  const filterLabel = [period?.label, selItem ? `${fmtDate(selItem.start_local, loc)} · ${selItem.name}` : null]
    .filter(Boolean)
    .join(" · ");
  const anyFilter = !!period || !!selectedItem;

  // Reparto del tiempo por zona de FC (5 zonas de Strava), aproximado por la FC media.
  const hrz = zones?.heart_rate_zones ?? [];
  const totalZoneMin = periodItems
    .filter((r) => hrZone(r.avg_heartrate, hrz) > 0)
    .reduce((s, r) => s + r.duration_min, 0);
  const zonas = [1, 2, 3, 4, 5]
    .map((z) => {
      const mins = periodItems
        .filter((r) => hrZone(r.avg_heartrate, hrz) === z)
        .reduce((s, r) => s + r.duration_min, 0);
      return { z, name: "Z" + z, label: t("zone_" + z), mins, pct: totalZoneMin ? mins / totalZoneMin : 0 };
    })
    .filter((d) => d.mins > 0);
  const zoneColors = HR_ZONE_COLORS[cat];
  const showZones = cat !== "Hike";

  if (items.length === 0) return <Card>{t("txt_sin_disciplina", { cat: t("cat_" + cat) })}</Card>;

  const zonesCard = showZones && zonas.length > 0 && (
    <Card>
      <SectionTitle>{t("sec_zonas")}</SectionTitle>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={zonas} dataKey="mins" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={3}>
            {zonas.map((z) => (
              <Cell key={z.name} fill={zoneColors[z.z - 1]} />
            ))}
          </Pie>
          <Tooltip content={<ZoneTooltip t={t} palette={palette} />} />
          <Legend
            wrapperStyle={{ fontSize: 12 }}
            formatter={(value: string, entry: any) => `${value} · ${entry?.payload?.label ?? ""}`}
          />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  );

  return (
    <div className="space-y-6">
      {anyFilter && (
        <div className="flex">
          <FilterChip
            label={filterLabel}
            onClear={() => {
              setPeriod(null);
              setSelectedItem(null);
            }}
            color={COLOR}
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        {kpis.map((k) => (
          <Kpi key={k.label} label={k.label} value={k.value} accent info={k.info} />
        ))}
      </div>

      <Card>
        <div className="flex items-center justify-between">
          <SectionTitle>{t("sec_seguimiento")}</SectionTitle>
          <span className="hidden text-xs text-muted sm:block">
            {t(granularity === "day" ? "hint_chart_day" : "hint_chart")}
          </span>
        </div>
        <MetricTimeline
          acts={items}
          weeks={weeks}
          metrics={metrics}
          resetKey={acts}
          onPeriod={setPeriod}
          accent={COLOR}
          granularity={granularity}
        />
      </Card>

      {hasSessionTrend ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between">
              <SectionTitle>{trendTitle}</SectionTitle>
              <span className="hidden text-xs text-muted sm:block">{t("hint_punto")}</span>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={serie}
                className="cursor-crosshair select-none"
                {...paceMouse}
                onClick={(s: any) => {
                  if (draggedRef.current) {
                    draggedRef.current = false;
                    return;
                  }
                  const id = s?.activePayload?.[0]?.payload?.id as string | undefined;
                  if (id) setSelectedItem((cur) => (cur === id ? null : id));
                }}
              >
                <CartesianGrid stroke={palette.grid} strokeOpacity={0.5} vertical={false} />
                <XAxis
                  dataKey="idx"
                  type="number"
                  domain={[0, Math.max(serie.length - 1, 0)]}
                  {...axisProps(palette)}
                  allowDecimals={false}
                  minTickGap={40}
                  tickFormatter={(v) => {
                    const point = serie.find((p) => p.idx === Number(v));
                    return point ? fmtDMY(localDateString(point.ts), loc) : "";
                  }}
                />
                <YAxis
                  {...axisProps(palette)}
                  reversed={trendKind ? trendReversed(trendKind) : false}
                  domain={["auto", "auto"]}
                  tickFormatter={(v) => fmtTrend(Number(v))}
                  width={70}
                />
                <Tooltip
                  {...tipProps(palette)}
                  formatter={(v: number) => fmtTrend(Number(v))}
                  labelFormatter={(_, payload) => {
                    const ts = payload?.[0]?.payload?.ts;
                    return typeof ts === "number" ? fmtDate(localDateString(ts), loc) : "";
                  }}
                />
                <ReferenceArea
                  x1={dragLo}
                  x2={dragHi}
                  fill={COLOR}
                  fillOpacity={0.25}
                  stroke={COLOR}
                  strokeOpacity={0.9}
                  strokeWidth={1.5}
                  ifOverflow="visible"
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="val"
                  name={trendLineName}
                  stroke={COLOR}
                  strokeWidth={2}
                  isAnimationActive={false}
                  dot={(props: any) => {
                    const sel = props.payload?.id === selectedItem;
                    return (
                      <circle
                        key={props.payload?.id ?? props.index}
                        cx={props.cx}
                        cy={props.cy}
                        r={sel ? 5 : 2.5}
                        fill={COLOR}
                        stroke={sel ? palette.tipBg : "none"}
                        strokeWidth={sel ? 2 : 0}
                      />
                    );
                  }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
          {zonesCard}
        </div>
      ) : (
        zonesCard
      )}

      <Card>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <SectionTitle>{anyFilter ? t("sec_acts_periodo") : t("sec_ultimas")}</SectionTitle>
          <span className="text-xs text-muted">
            {t("txt_n_acts", { n: tableActs.length })} · {t("hint_fila")}
          </span>
        </div>
        <ActivityTable acts={tableActs} gearMap={gearMap} paceKind={cat === "Hike" ? null : trendKind} />
      </Card>
    </div>
  );
}
